import type { ListBuilderConfiguration } from '../types';

/**
 * Price Contract Item list configuration
 * Shows items associated with a price contract
 * Features dynamic color rules and edit/delete actions
 * Converted from JSON to TypeScript for type safety
 */
export const PRICE_CONTRACT_ITEM_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-price-contract-items-all',
			cacheTTL: 600000,
			name: 'All Price Contract Items',
			type: 'default',
			active: true,
			bulkActionTemplate: 'priceContractItems',
			columns: [
				{
					id: 'description-manufacturer',
					fields: [
						{
							id: 'description-field',
							link: {
								type: 'custom',
								enabled: true,
								urlTemplate: '/items/{item.id}',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'item.description',
									objectTable: 'price_contract_item',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'manufacturer-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'item.manufacturer',
									objectTable: 'price_contract_item',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h1-1', text: 'Description' },
						{ id: 'h1-2', text: 'Manufacturer' },
					],
					minWidth: 200,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'description',
							objectTable: 'item',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'item-number',
					fields: [
						{
							id: 'item-number-field',
							dataConfig: {
								type: 'conditional',
								conditions: [
									{
										field: 'item.externalId2',
										operator: 'not_equals',
										value: null,
										dataConfig: {
											type: 'field',
											field: {
												fieldName: 'item.externalId2',
												objectTable: 'price_contract_item',
											},
										},
									},
									{
										field: 'item.externalId',
										operator: 'not_equals',
										value: null,
										dataConfig: {
											type: 'field',
											field: {
												fieldName: 'item.externalId',
												objectTable: 'price_contract_item',
											},
										},
									},
								],
								defaultDataConfig: {
									type: 'field',
									field: {
										fieldName: 'item.id',
										objectTable: 'price_contract_item',
									},
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h2', text: 'Item #' }],
					minWidth: 120,
					sortable: false,
					alignment: 'left',
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'minimum-quantity',
					fields: [
						{
							id: 'minimum-quantity-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.minimumQuantityFormatted',
									objectTable: 'price_contract_item',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h5', text: 'Min Order Qty' }],
					minWidth: 120,
					sortable: false,
					alignment: 'center',
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'middle',
				},
				{
					// When the contract doesn't affect price (affectsPrice false), the
					// three content fields hide via conditionalVisibility and a single
					// "-" placeholder shows instead of "- / Individual Price / -".
					// (The placeholder is needed because the column renderer's
					// empty-state "-" only fires when a column has zero field elements,
					// not when its fields conditionally render nothing.)
					id: 'price',
					fields: [
						{
							id: 'price-empty-placeholder',
							dataConfig: {
								type: 'custom_text',
								customText: '-',
							},
							conditionalVisibility: {
								field: 'affectsPrice',
								condition: 'not_equals',
								value: true,
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'contract-price-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.contractPriceFormatted',
									objectTable: 'price_contract_item',
								},
							},
							conditionalVisibility: {
								field: 'affectsPrice',
								condition: 'equals',
								value: true,
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'price-origin-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.priceOrigin',
									objectTable: 'price_contract_item',
								},
								defaultValue: 'Individual Price',
							},
							conditionalVisibility: {
								field: 'affectsPrice',
								condition: 'equals',
								value: true,
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-2',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'price-change-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.priceChangeFormatted',
									objectTable: 'price_contract_item',
								},
							},
							conditionalVisibility: {
								field: 'affectsPrice',
								condition: 'equals',
								value: true,
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'dynamic',
									colorField: 'calculated.priceChangeColor',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h6', text: 'Price' }],
					minWidth: 200,
					sortable: false,
					alignment: 'right',
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					// How the contract affects cost (acquisition + sell), mirroring
					// the Price column. The whole column — header and cells — is
					// hidden for users without the cost-view permission, and the
					// backend only enriches these fields for permitted callers.
					// Each dimension's lines render only when the contract actually
					// overrides that cost (affects* flags); when neither is set, a
					// single "-" placeholder shows instead (gated on the combined
					// affectsCost flag, mirroring the Price column).
					id: 'cost',
					requiredPermission: 'price_contracts:costs:view',
					fields: [
						{
							id: 'cost-empty-placeholder',
							dataConfig: {
								type: 'custom_text',
								customText: '-',
							},
							conditionalVisibility: {
								field: 'affectsCost',
								condition: 'not_equals',
								value: true,
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'acquisition-cost-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.acquisitionCostFormatted',
									objectTable: 'price_contract_item',
								},
							},
							prefix: 'Acquisition: ',
							conditionalVisibility: {
								field: 'affectsAcquisitionCost',
								condition: 'equals',
								value: true,
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'cost-line-break-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'acquisition-cost-description-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.acquisitionCostDescription',
									objectTable: 'price_contract_item',
								},
							},
							conditionalVisibility: {
								field: 'affectsAcquisitionCost',
								condition: 'equals',
								value: true,
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'cost-line-break-2',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'sell-cost-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.sellCostFormatted',
									objectTable: 'price_contract_item',
								},
							},
							prefix: 'Sell: ',
							conditionalVisibility: {
								field: 'affectsSellCost',
								condition: 'equals',
								value: true,
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'cost-line-break-3',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'sell-cost-description-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'calculated.sellCostDescription',
									objectTable: 'price_contract_item',
								},
							},
							conditionalVisibility: {
								field: 'affectsSellCost',
								condition: 'equals',
								value: true,
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h7', text: 'Cost' }],
					minWidth: 200,
					sortable: false,
					alignment: 'right',
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'edit',
									variant: 'primary',
									visibility: 'visible',
									conditionalVisibility: {
										field: 'linkType',
										condition: 'equals',
										value: 'direct',
									},
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'delete',
									variant: 'danger',
									visibility: 'visible',
									conditionalVisibility: {
										field: 'linkType',
										condition: 'equals',
										value: 'direct',
									},
								},
							],
						},
					],
					headers: [{ id: 'actions-header', text: '' }],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			defaultSortId: 'column-description-manufacturer-asc',
			entitySettings: {
				objectType: 'item',
				viewable: true,
			},
			requiredFields: [
				'item._id',
				'item.description',
				'item.manufacturer',
				'item.externalId',
				'item.externalId2',
				'item.price',
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100, 200],
	},
	filters: {
		activeFilters: [
			'itemSearch',
			'itemCompany',
			'itemCategories',
			'itemManufacturers',
			'itemGroups',
			'itemStatus',
			'stock_status',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'manufacturer', fieldLabel: 'Manufacturer' },
				{ fieldName: 'externalId', fieldLabel: 'Item #' },
				{ fieldName: 'price', fieldLabel: 'Price' },
			],
		},
	],
};
